import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/form-field';
import { Card, CardContent, CardHeader, CardTitle, Input, Select, Skeleton } from '@/components/ui/primitives';
import { api } from '@/lib/api';

interface Named {
  name: string;
  count: number;
}
interface MinutesRow {
  name: string;
  minutes: number;
  billable: number;
}
interface Summary {
  tickets: {
    created: number;
    resolved: number;
    openNow: number;
    byArea: Named[];
    byPriority: Named[];
    bySource: Named[];
    avgFirstResponseMinutes: number | null;
    avgResolutionHours: number | null;
    sla: { tickets: number; responseBreached: number; resolutionBreached: number; compliancePct: number | null };
  };
  weekly: { week: string; created: number; resolved: number }[];
  repairs: { received: number; ready: number; pickedUp: number; avgTurnaroundDays: number | null; byOutcome: Named[] };
  time: { totalMinutes: number; billableMinutes: number; byUser: MinutesRow[]; byCustomer: MinutesRow[] };
  monitoring: { problems: number; withTicket: number; bySeverity: Named[]; topHosts: Named[] };
  contracts: { active: number; started: number; expiringIn60Days: number };
}

type Preset = 'month' | 'lastMonth' | 'quarter' | 'year' | 'last30' | 'custom';

function presetRange(p: Preset): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  switch (p) {
    case 'lastMonth':
      return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 1)) };
    case 'quarter':
      return { from: iso(new Date(y, Math.floor(m / 3) * 3, 1)), to: iso(new Date(y, Math.floor(m / 3) * 3 + 3, 1)) };
    case 'year':
      return { from: iso(new Date(y, 0, 1)), to: iso(new Date(y + 1, 0, 1)) };
    case 'last30':
      return { from: iso(new Date(y, m, now.getDate() - 29)), to: iso(new Date(y, m, now.getDate() + 1)) };
    default:
      return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 1)) };
  }
}

const hours = (min: number) => `${(min / 60).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Std.`;

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Bars({ items, empty = 'Keine Daten' }: { items: Named[]; empty?: string }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (items.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ul className="grid gap-2">
      {items.map((i) => (
        <li key={i.name} className="grid grid-cols-[minmax(0,10rem)_1fr_3rem] items-center gap-3 text-sm">
          <span className="truncate" title={i.name}>
            {i.name}
          </span>
          <span className="h-2.5 rounded-full bg-muted">
            <span className="block h-2.5 rounded-full bg-primary" style={{ width: `${(i.count / max) * 100}%` }} />
          </span>
          <span className="text-right tabular-nums">{i.count}</span>
        </li>
      ))}
    </ul>
  );
}

function WeeklyChart({ data }: { data: Summary['weekly'] }) {
  if (data.length === 0) return <p className="text-sm text-muted-foreground">Keine Daten</p>;
  const max = Math.max(1, ...data.flatMap((d) => [d.created, d.resolved]));
  const w = 100 / data.length;
  return (
    <div>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-40 w-full" role="img" aria-label="Tickets je Woche: erstellt und gelöst">
        {data.map((d, i) => (
          <g key={d.week}>
            <rect x={i * w + w * 0.12} y={40 - (d.created / max) * 38} width={w * 0.36} height={(d.created / max) * 38} className="fill-primary">
              <title>{`Woche ab ${new Date(d.week).toLocaleDateString('de-DE')}: ${d.created} erstellt`}</title>
            </rect>
            <rect x={i * w + w * 0.52} y={40 - (d.resolved / max) * 38} width={w * 0.36} height={(d.resolved / max) * 38} className="fill-success">
              <title>{`Woche ab ${new Date(d.week).toLocaleDateString('de-DE')}: ${d.resolved} gelöst`}</title>
            </rect>
          </g>
        ))}
      </svg>
      <div className="mt-2 flex justify-between text-xs text-muted-foreground">
        <span>{new Date(data[0]!.week).toLocaleDateString('de-DE')}</span>
        <span className="flex gap-3">
          <span className="inline-flex items-center gap-1">
            <span className="size-2 rounded-sm bg-primary" /> erstellt
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="size-2 rounded-sm bg-success" /> gelöst
          </span>
        </span>
        <span>{new Date(data.at(-1)!.week).toLocaleDateString('de-DE')}</span>
      </div>
    </div>
  );
}

function MinutesTable({ rows }: { rows: MinutesRow[] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">Keine Zeitbuchungen</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground">
        <tr>
          <th className="py-1 text-left font-medium" />
          <th className="py-1 text-right font-medium">gesamt</th>
          <th className="py-1 text-right font-medium">abrechenbar</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} className="border-t">
            <td className="max-w-[12rem] truncate py-1.5">{r.name}</td>
            <td className="py-1.5 text-right tabular-nums">{hours(r.minutes)}</td>
            <td className="py-1.5 text-right tabular-nums">{hours(r.billable)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ReportsPage() {
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState(presetRange('month'));
  const range = useMemo(() => (preset === 'custom' ? custom : presetRange(preset)), [preset, custom]);
  const fromIso = new Date(`${range.from}T00:00:00`).toISOString();
  const toIso = new Date(`${range.to}T00:00:00`).toISOString();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', fromIso, toIso],
    queryFn: () => api<Summary>(`/reports/summary?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`),
    enabled: range.to > range.from,
  });
  const exportUrl = (type: string) => `/api/reports/export?type=${type}&from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`;

  return (
    <div>
      <PageHeader
        title="Berichte"
        description="Kennzahlen zu Tickets, SLA, Reparaturen, Zeiten, Monitoring und Verträgen."
        actions={
          <>
            <Button variant="outline" asChild>
              <a href={exportUrl('time')}>
                <Download /> Zeiten (CSV)
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href={exportUrl('tickets')}>
                <Download /> Tickets (CSV)
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href={exportUrl('repairs')}>
                <Download /> Reparaturen (CSV)
              </a>
            </Button>
          </>
        }
      />
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Select className="w-auto" value={preset} onChange={(e) => setPreset(e.target.value as Preset)} aria-label="Zeitraum">
          <option value="month">Dieser Monat</option>
          <option value="lastMonth">Letzter Monat</option>
          <option value="last30">Letzte 30 Tage</option>
          <option value="quarter">Dieses Quartal</option>
          <option value="year">Dieses Jahr</option>
          <option value="custom">Eigener Zeitraum</option>
        </Select>
        {preset === 'custom' && (
          <>
            <Input type="date" className="w-auto" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} aria-label="Von" />
            <span className="text-sm text-muted-foreground">bis (ausschließlich)</span>
            <Input type="date" className="w-auto" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} aria-label="Bis" />
          </>
        )}
      </div>

      {isError && <p className="text-sm text-destructive">Der Bericht konnte nicht geladen werden.</p>}
      {isLoading || !data ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid gap-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi label="Tickets erstellt" value={data.tickets.created} />
            <Kpi label="Tickets gelöst" value={data.tickets.resolved} />
            <Kpi label="Derzeit offen" value={data.tickets.openNow} />
            <Kpi label="Ø erste Antwort" value={data.tickets.avgFirstResponseMinutes != null ? hours(data.tickets.avgFirstResponseMinutes) : '–'} />
            <Kpi label="Ø Lösungszeit" value={data.tickets.avgResolutionHours != null ? `${data.tickets.avgResolutionHours.toLocaleString('de-DE')} Std.` : '–'} />
            <Kpi
              label="SLA eingehalten"
              value={data.tickets.sla.compliancePct != null ? `${data.tickets.sla.compliancePct.toLocaleString('de-DE')} %` : '–'}
              hint={`${data.tickets.sla.tickets} Tickets mit SLA`}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Tickets je Woche</CardTitle>
            </CardHeader>
            <CardContent>
              <WeeklyChart data={data.weekly} />
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Nach Bereich</CardTitle>
              </CardHeader>
              <CardContent>
                <Bars items={data.tickets.byArea} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Nach Priorität</CardTitle>
              </CardHeader>
              <CardContent>
                <Bars items={data.tickets.byPriority} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Nach Quelle</CardTitle>
              </CardHeader>
              <CardContent>
                <Bars items={data.tickets.bySource} />
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">
                  Zeiten · {hours(data.time.totalMinutes)} gesamt, {hours(data.time.billableMinutes)} abrechenbar
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs font-medium text-muted-foreground">Nach Mitarbeiter</p>
                  <MinutesTable rows={data.time.byUser} />
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium text-muted-foreground">Nach Kunde</p>
                  <MinutesTable rows={data.time.byCustomer} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Reparaturen</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Kpi label="Angenommen" value={data.repairs.received} />
                  <Kpi label="Fertig" value={data.repairs.ready} />
                  <Kpi label="Abgeholt" value={data.repairs.pickedUp} />
                  <Kpi label="Ø Durchlauf" value={data.repairs.avgTurnaroundDays != null ? `${data.repairs.avgTurnaroundDays.toLocaleString('de-DE')} T.` : '–'} />
                </div>
                <Bars items={data.repairs.byOutcome} empty="Keine abgeschlossenen Reparaturen" />
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">
                  Monitoring · {data.monitoring.problems} Probleme, {data.monitoring.withTicket} mit Ticket
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs font-medium text-muted-foreground">Nach Schweregrad</p>
                  <Bars items={data.monitoring.bySeverity} />
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium text-muted-foreground">Häufigste Hosts</p>
                  <Bars items={data.monitoring.topHosts} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Verträge</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3">
                <Kpi label="Wirksam (heute)" value={data.contracts.active} />
                <Kpi label="Begonnen im Zeitraum" value={data.contracts.started} />
                <Kpi label="Enden in 60 Tagen" value={data.contracts.expiringIn60Days} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
